import mergeRefs from 'merge-refs';
import { type ElementRef, useEffect, useMemo, useRef, useState } from 'react';

import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useInViewport } from '@/hooks/useInViewport';
import { afterFrames } from '@/lib/animate';

interface TruncateOptions {
  /** Maximum number of lines to show when content is truncated. Use 'auto' to calculate based on viewport height */
  maxLines: number | 'auto';
  /** Whether content needs truncation (skips measurement if provided - useful for caching results) */
  needsTruncation?: boolean;
  /** Whether content should start in expanded state */
  initiallyExpanded?: boolean;
  /** Skip measurement when expanded or not in viewport (useful for performance optimization) */
  lazyMeasurement?: boolean;
  /** Callback fired when truncation capability changes */
  onTruncationChange(needsTruncation: boolean): void;
  /** Callback fired when expanded state changes */
  onExpand?(isExpanded: boolean): void;
}

const lineHeightCache = new Map<string, number>();

/**
 * Hook for text truncation with expand/collapse functionality using CSS line-clamp.
 *
 * Provides automatic measurement to determine if content needs truncation, handles
 * viewport resize events, and manages expand/collapse state with smooth transitions.
 *
 * @param opts - Configuration options for truncation behavior
 * @returns Object containing ref for the truncated element, expand state, and toggle function
 *
 * @example
 * ```tsx
 * const [needsTruncation, setNeedsTruncation] = useState<boolean | undefined>(undefined); // Cache measurement result
 * const [isExpanded, setIsExpanded] = useState(false);
 * const { truncateElementRef, toggleExpand } = useTruncate({
 *   maxLines: 'auto',
 *   needsTruncation,
 *   onTruncationChange: setNeedsTruncation,
 *   onExpand: setIsExpanded
 * });
 *
 * return (
 *   <div>
 *     <div ref={truncateElementRef}>
 *       Long text content that will be truncated...
 *     </div>
 *     {needsTruncation && (
 *       <button onClick={toggleExpand}>
 *         {isExpanded ? 'Show Less' : 'Show More'}
 *       </button>
 *     )}
 *   </div>
 * );
 * ```
 */

export function useTruncate(opts: TruncateOptions) {
  const truncateElementRef = useRef<ElementRef<'div'>>(null);
  const shouldApplyTruncation = opts.needsTruncation !== false;
  const shouldTrackViewport =
    shouldApplyTruncation &&
    (opts.lazyMeasurement || opts.needsTruncation === undefined);
  const { ref: inViewportRef, inViewport } = useInViewport({
    enabled: shouldTrackViewport,
  });
  const mergedRefs = useMemo(
    () => mergeRefs(inViewportRef, truncateElementRef),
    [inViewportRef, truncateElementRef]
  );
  const isMeasuringRef = useRef(false);
  const [isExpanded, setIsExpanded] = useState(opts.initiallyExpanded ?? false);

  const getLineHeight = (element: HTMLDivElement | null) => {
    try {
      if (!element) throw new Error('element not found');

      const style = window.getComputedStyle(element as Element);
      const lineHeight = style.lineHeight;
      if (lineHeight.endsWith('px')) {
        const value = parseFloat(lineHeight);
        if (!Number.isNaN(value)) return value;
      }

      const cacheKey = [
        style.fontFamily,
        style.fontSize,
        style.fontWeight,
        lineHeight,
      ].join('|');
      const cached = lineHeightCache.get(cacheKey);
      if (cached !== undefined) return cached;

      const value = parseFloat(lineHeight.replace('px', ''));

      if (Number.isNaN(value)) throw new Error('not a number');

      lineHeightCache.set(cacheKey, value);
      return value;
    } catch (_error) {
      return 24;
    }
  };

  const truncate = (
    element: HTMLDivElement | null,
    maxLines: number | 'auto' = 'auto'
  ) => {
    if (!element) return;

    const lines =
      maxLines === 'auto'
        ? Math.max(
            3,
            Math.round(window.innerHeight / getLineHeight(element) / 2)
          )
        : Math.max(1, Math.round(maxLines));

    element.style.setProperty('overflow', 'hidden');
    element.style.setProperty('display', '-webkit-box');
    element.style.setProperty('-webkit-box-orient', 'vertical');
    element.style.setProperty('-webkit-line-clamp', lines.toString());
  };

  const expand = (element: HTMLDivElement | null) => {
    if (!element) return;
    element.style.removeProperty('overflow');
    element.style.removeProperty('display');
    element.style.removeProperty('-webkit-box-orient');
    element.style.removeProperty('-webkit-line-clamp');
  };

  const applyMeasurementGuards = (element: HTMLDivElement | null) => {
    if (!element) return;
    element.style.setProperty('visibility', 'hidden');
    element.style.setProperty('position', 'absolute');
    element.style.setProperty('top', '0');
    element.style.setProperty('left', '0');
    element.style.setProperty('width', '100%');

    // Prevent interactivity and a11y duplication during measurement
    element.style.setProperty('pointer-events', 'none');
    element.style.setProperty('touch-action', 'none');
    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('inert', '');
    // Prevent duplicate IDs in the DOM
    element.removeAttribute('id');
    element.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
  };

  const measure = useDebouncedCallback(
    (onMeasure?: () => void) => {
      if (!shouldApplyTruncation) return;
      if (isMeasuringRef.current) return;
      isMeasuringRef.current = true;

      afterFrames(3, () => {
        if (!truncateElementRef.current) {
          isMeasuringRef.current = false;
          return;
        }

        // Store current expanded state
        const wasExpanded = isExpanded;
        let truncatedHeight: number;

        if (wasExpanded) {
          const truncatedClone = truncateElementRef.current.cloneNode(
            true
          ) as HTMLDivElement;
          applyMeasurementGuards(truncatedClone);
          truncate(truncatedClone, opts.maxLines);
          truncateElementRef.current.append(truncatedClone);

          truncatedHeight = truncatedClone.getBoundingClientRect().height;
          truncatedClone.remove();
        } else {
          truncatedHeight =
            truncateElementRef.current.getBoundingClientRect().height;
        }

        // Create measure element for full height
        const measureElement = truncateElementRef.current.cloneNode(
          true
        ) as HTMLDivElement;

        truncateElementRef.current.style.setProperty('position', 'relative');
        applyMeasurementGuards(measureElement);

        expand(measureElement);
        truncateElementRef.current.append(measureElement);

        afterFrames(1, () => {
          const fullHeight = measureElement.getBoundingClientRect().height;
          const overflowHeight = Math.max(0, fullHeight - truncatedHeight);
          const needsTruncation = overflowHeight > 1;

          opts?.onTruncationChange(needsTruncation);
          measureElement.remove();
          isMeasuringRef.current = false;

          onMeasure?.();
        });
      });
    },
    {
      delay: 100,
      flushOnUnmount: true,
    }
  );

  useEffect(() => {
    if (!shouldApplyTruncation) {
      expand(truncateElementRef.current);
      return;
    }

    if (!truncateElementRef.current) return;
    opts.initiallyExpanded
      ? expand(truncateElementRef.current)
      : truncate(truncateElementRef.current, opts.maxLines);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.initiallyExpanded, opts.maxLines, shouldApplyTruncation]);

  useEffect(() => {
    // Skip measurement when expanded or not in viewport (if lazy measurement enabled)
    if (!shouldApplyTruncation) return;
    if (opts.lazyMeasurement && (!inViewport || isExpanded)) return;

    // Skip if already have a truncation result
    if (opts.needsTruncation !== undefined) return;
    measure();
  }, [
    opts.needsTruncation,
    opts.lazyMeasurement,
    inViewport,
    isExpanded,
    measure,
    shouldApplyTruncation,
  ]);

  const rect = useRef<[number, number]>([0, 0]);

  /** Measure when resized */
  useEffect(() => {
    const element = truncateElementRef.current;
    if (!shouldApplyTruncation) return;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      // Only measure when visible
      if (!inViewport) return;

      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const [w, h] = rect.current;

        if (w === width && h === height) return;
        rect.current = [width, height];

        if (w === 0 || h === 0) {
          return;
        }

        // Re-apply truncation if element is currently truncated and maxLines is 'auto'
        if (!isExpanded && opts.maxLines === 'auto') {
          truncate(element, opts.maxLines);
        }

        measure();
      }
    });
    observer.observe(element);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.maxLines, inViewport, isExpanded, measure, shouldApplyTruncation]);

  /** Handle window resize for 'auto' maxLines */
  useEffect(() => {
    if (opts.maxLines !== 'auto') return;
    if (!shouldApplyTruncation) return;

    let raf = 0;

    const reapply = () => {
      if (!inViewport || isExpanded) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const element = truncateElementRef.current;
        if (!element) return;
        truncate(element, opts.maxLines);
        measure();
      });
    };

    window.addEventListener('resize', reapply);

    return () => {
      window.removeEventListener('resize', reapply);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.maxLines, inViewport, isExpanded, measure, shouldApplyTruncation]);

  return {
    truncateElementRef: mergedRefs,
    isExpanded,
    measure,
    toggleExpand() {
      if (!shouldApplyTruncation) return;

      setIsExpanded((isExpanded) => {
        const newExpandedState = !isExpanded;
        newExpandedState
          ? expand(truncateElementRef.current)
          : truncate(truncateElementRef.current, opts.maxLines);
        opts.onExpand?.(newExpandedState);
        return newExpandedState;
      });
    },
  };
}
